import React from "react";
import { useApp } from "@/context/AppProvider";
import { Reveal } from "@/components/motion";
import { EditableText, ListControls, AddButton } from "@/components/editable";
import { TEMPLATES } from "@/content";
import { ArrowUpRight } from "lucide-react";

const spans = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5", "md:col-span-7"];

export const Projects = () => {
  const { tr, ed, editMode, moveItem, removeItem, addItem } = useApp();
  const path = ["projects", "items"];
  const items = tr.projects.items;

  return (
    <section id="projects" data-testid="projects" className="relative py-24 md:py-32 lg:py-40" style={{ backgroundColor: "var(--bone)" }}>
      <div className="container-lux">
        <Reveal className="max-w-2xl mb-16">
          <EditableText as="p" path={["projects", "eyebrow"]} value={tr.projects.eyebrow} className="eyebrow mb-4 inline-block" />
          <EditableText as="h2" path={["projects", "title"]} value={tr.projects.title} className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.1] block" style={{ color: "var(--ink)" }} />
        </Reveal>

        <div className="grid md:grid-cols-12 gap-5 auto-rows-[240px] md:auto-rows-[260px]">
          {items.map((p, i) => (
            <Reveal key={i} delay={i * 0.08} className={spans[i % spans.length]}>
              <div data-testid={`project-${p.id || i}`} className="group relative w-full h-full overflow-hidden rounded-2xl shadow-soft">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 transition-opacity duration-500" style={{ background: "linear-gradient(to top, rgba(19,19,19,0.85) 0%, rgba(19,19,19,0.1) 55%, transparent 100%)" }} />

                {editMode && (
                  <ListControls
                    className="absolute top-3 start-3"
                    onUp={() => moveItem(path, i, -1)}
                    onDown={() => moveItem(path, i, 1)}
                    onDelete={() => removeItem(path, i)}
                    canUp={i > 0}
                    canDown={i < items.length - 1}
                    testid={`project-controls-${i}`}
                  />
                )}

                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                  <div className="flex items-end justify-between gap-4">
                    <div className="flex-1">
                      <EditableText as="p" path={[...path, i, "tag"]} value={p.tag} className="text-[11px] uppercase tracking-[0.25em] mb-2 block" style={{ color: "var(--gold-bright)" }} />
                      <EditableText as="h3" path={[...path, i, "name"]} value={p.name} className="font-display text-2xl md:text-3xl text-white leading-tight max-w-md block" />
                      {editMode && (
                        <EditableText as="p" path={[...path, i, "image"]} value={p.image} className="mt-2 text-[10px] text-white/70 block break-all bg-black/40 rounded px-2 py-1" />
                      )}
                    </div>
                    {!editMode && (
                      <span className="shrink-0 w-11 h-11 rounded-full glass flex items-center justify-center translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                        <ArrowUpRight size={18} className="text-white" />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10">
          <AddButton onClick={() => addItem(path, TEMPLATES.projectItem.en, TEMPLATES.projectItem.ar)} label={ed.addItem} testid="add-project" />
        </div>
      </div>
    </section>
  );
};
